import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { Button } from '@mui/material';

import { useIntl } from 'react-intl';
import { Col, Row } from 'react-bootstrap';

import Preloader from '../../../components/other/Preloader/Preloader';
import AlertDialog from '../../../components/other/Dialog/AlertDialog';
import CategoriesTable from './components/CategoriesTable';
import { actions as categoriesActions } from '../../../store/ducks/categories.duck';
import { IAppState } from '../../../store/rootDuck';
import { setLayoutFooter, setLayoutSubheader } from '../../../utils/layout';
import { categoriesBreadCrumbs } from './utils/createBreadcrumbs';
import { actions as profileActions } from '../../../store/ducks/profile.duck';
import useCrudSnackbar from '../../../hooks/useCrudSnackbar';
import { useDefineUserRole } from '../../../hooks';
import { useStylesCategoriesList } from './hooks/useStyles';
import { ICategoryItem } from './interfaces';

const CategoriesListPage: React.FC<TPropsFromRedux> = ({
  page,
  perPage,
  total,

  fetch,
  clearFetch,
  categories,
  categoriesLoading,

  fetchParent,
  clearParent,
  parent,
  parentLoading,

  deleteCategory,
  clearDelete,
  deleteLoading,
  deleteSuccess,
  deleteError,

  fetchMe,
  clearMe,
  me,
  loadingMe,

  setPositions,
}) => {
  const intl = useIntl();
  const classes1 = useStylesCategoriesList();
  const { classes } = classes1;
  const navigate = useNavigate();
  const { parentId } = useParams();

  const [deleteId, setDeleteId] = useState<number | undefined>(-1);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [lang, setLang] = useState('ru');
  const isManager = useDefineUserRole(me, 'ROLE_MANAGER');

  const handleDeleteDialog = useCallback((id: number | undefined) => {
    setDeleteId(id);
    setAlertOpen(true);
  }, []);

  const deleteAction = useCallback(() => {
    setAlertOpen(false);
    deleteCategory({ id: deleteId });
  }, [deleteId, deleteCategory]);

  const editAction = useCallback(
    (item: ICategoryItem) => {
      navigate(`/categories/edit/${item.id}/${parentId || ''}`);
    },
    [parentId, navigate]
  );

  const productListAction = useCallback(
    (item: ICategoryItem) => {
      navigate(`/products/list/${item.id}`, {
        state: {
          categoryId: item.id,
        },
      })
    },
    [navigate]
  );

  const categoryProductListAction = useCallback(() => {
    navigate(`/products/list/${parentId}`);
  }, [parentId, navigate]);

  const pushAction = useCallback(
    (parentIdProp?: string) => {
      navigate(`${parentIdProp ? `/categories/new/${parentIdProp}` : '/categories/new'}`);
    },
    [navigate]
  );

  const productAction = useCallback(() => {
    const path = parentId ? `/products/create/${parentId}` : '/products/create';
    navigate(path);
  }, [parentId, navigate]);

  setLayoutSubheader({
    title: parent?.name || intl.formatMessage({ id: 'MENU.CATALOG.CATEGORIES.LIST' }),
    breadcrumb: parent?.name ? categoriesBreadCrumbs(parent?.parent, intl) : [],
  });
  setLayoutFooter({ show: true });

  useEffect(() => {
    fetchMe();
    return () => {
      clearMe();
      clearParent();
      clearDelete();
      clearFetch();
    };
  }, []);

  useEffect(() => {
    clearFetch();
  }, [parentId]);

  useEffect(() => {
    fetch({ page: 1, perPage: 20, parentId: parentId ? +parentId : undefined });
  }, [fetch, parentId, fetchParent]);

  useEffect(() => {
    if (parentId) {
      fetchParent({ parentId: Number.parseInt(parentId, 10) });
    } else {
      clearParent();
    }
  }, [parentId, fetchParent]);

  useCrudSnackbar({
    success: deleteSuccess,
    error: deleteError,
    clear: clearDelete,
    successMessage: intl.formatMessage({ id: 'CATEGORY.SNACKBAR.DELETE' }),
    errorMessage: `${intl.formatMessage({ id: 'CATEGORIES.DELETE.ERROR' })}`,
    afterSuccess: () => {
      fetch({ page, perPage, parentId: parentId ? +parentId : undefined });
    },
    afterSuccessOrError: () => {
      clearDelete();
    },
  });

  const showAddProductButton = !!parentId;

  if (categoriesLoading || parentLoading || loadingMe || deleteLoading || !categories) {
    return <Preloader />;
  }

  return (
    <>
      <Row className={classes.actions}>
        <div className={classes.containerBtns} style={{ marginRight: 20 }}>
          {me?.is_admin && (
            <Col md='auto' style={{marginRight:20}}>
              <Button color='primary' variant='contained' onClick={() => pushAction(parentId)}>
                {intl.formatMessage({ id: 'CATEGORIES.BUTTON.ADD' })}
              </Button>
            </Col>
          )}
          {showAddProductButton && (
            <Col md='auto' style={{marginRight:20}}>
              <Button color='primary' variant='contained' onClick={productAction}>
                {intl.formatMessage({ id: 'CATEGORY.BUTTON.ADD.PRODUCT' })}
              </Button>
            </Col>
          )}
          {parentId && (
            <Col md='auto'>
              <Button color='primary' variant='contained' onClick={categoryProductListAction}>
                {intl.formatMessage({ id: 'CATEGORIES.TABLE.TOOLTIP.PRODUCTS' })}
              </Button>
            </Col>
          )}
        </div>
      </Row>

      <CategoriesTable
        page={page}
        perPage={perPage}
        total={total}
        categories={categories || []}
        handleDeleteDialog={handleDeleteDialog}
        editAction={editAction}
        parentId={parentId || ''}
        pushAction={pushAction}
        classes={classes}
        fetch={fetch}
        productListAction={productListAction}
        setPositions={setPositions}
        is_admin={me?.is_admin || false}
        isManager={isManager || false}
        lang={lang}
      />

      <AlertDialog
        open={isAlertOpen}
        message={intl.formatMessage({ id: 'CATEGORIES.DELETE.TEXT' })}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          setAlertOpen(false);
        }}
        handleAgree={() => deleteAction()}
      />
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    page: state.categories.page,
    perPage: state.categories.per_page,
    total: state.categories.total,

    categories: state.categories.categories,
    categoriesLoading: state.categories.categoriesLoading,

    parent: state.categories.parent,
    parentLoading: state.categories.parentLoading,

    deleteLoading: state.categories.deleteLoading,
    deleteSuccess: state.categories.deleteSuccess,
    deleteError: state.categories.deleteError,

    me: state.profile.me,
    loadingMe: state.profile.loading,
  }),
  {
    fetch: categoriesActions.fetchRootRequest,
    clearFetch: categoriesActions.clearRootRequest,

    fetchParent: categoriesActions.fetchParentRequest,
    clearParent: categoriesActions.clearParentRequest,

    deleteCategory: categoriesActions.deleteRequest,
    clearDelete: categoriesActions.clearDelete,

    setPositions: categoriesActions.setPositionsRequest,

    fetchMe: profileActions.fetchRequest,
    clearMe: profileActions.clearMe,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(CategoriesListPage);
